import React, { CSSProperties, useCallback, useMemo, useState, useEffect } from 'react';
import { FaOptionIcon, FaOptionIconProps, NamedIconsType } from '@europrocurement/l2d-icons';
import {
    Box,
    Collapse,
    lighten,
    PaletteColor,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { IconName } from '@fortawesome/sharp-regular-svg-icons';
import { useModalMenuContext } from '@europrocurement/flexy-components/hooks/useModalMenuContext';

export interface ModalMenuItemProps {
    identifier: string;
    label: string;
    icon?: FaOptionIconProps;
    onClick?: (target: string | null) => void;
    onSidebarClose?: () => void;
    color?: PaletteColor;
    childrenItems?: ModalMenuItemProps[];
    folded?: boolean;
    ancestors?: string[];
    header?: boolean;
    hidden?: boolean;
    active?: boolean;
}

/**
 * Version modale du MenuItem qui gère l'état actif via un contexte modal
 * au lieu de se baser sur l'URL
 */
export const ModalMenuItem: React.FunctionComponent<ModalMenuItemProps> = function (props) {
    const {
        identifier,
        label,
        icon,
        onClick = () => {},
        onSidebarClose = undefined,
        color,
        childrenItems = [],
        folded = false,
        ancestors = [],
        header = false,
        hidden = false,
        active = false,
    } = props;

    const theme = useTheme();
    const { openElements, setOpenElements } = useModalMenuContext();
    const [hover, setHover] = useState<boolean>(false);
    const [collapsed, setCollapsed] = useState<boolean>(openElements.indexOf(identifier) === -1);

    const activeElement = active;

    const { palette, breakpoints } = theme;
    const downSmBreakpoint = useMediaQuery(breakpoints.down('sm'));
    const mainColor = color || palette.primary;
    const boxClassNames = ['menu-item'];

    if (folded) {
        boxClassNames.push('menu-item-folded');
    }

    const childrenAncestors = [...ancestors, identifier];
    const childrenNodes = childrenItems.map((child) => (
        <ModalMenuItem
            key={child.identifier}
            {...child}
            folded={folded}
            ancestors={childrenAncestors}
        />
    ));

    /**
     * Vérifie récursivement si l'un des enfants est actif
     */
    const hasActiveChild = useCallback(
        (items: ModalMenuItemProps[]): boolean =>
            items.some((item) => {
                if (item.active) {
                    return true;
                }
                if (item.childrenItems && item.childrenItems.length > 0) {
                    return hasActiveChild(item.childrenItems);
                }
                return false;
            }),
        [],
    );

    /**
     * Effect qui ouvre automatiquement l'élément si un de ses enfants est actif
     * MAIS seulement au premier rendu, pas lors des interactions utilisateur
     */
    useEffect(() => {
        if (childrenItems.length > 0 && hasActiveChild(childrenItems)) {
            if (!openElements.includes(identifier)) {
                setOpenElements([...openElements, identifier]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Seulement au premier rendu

    /**
     * Synchronise l'état collapsed avec openElements
     */
    useEffect(() => {
        setCollapsed(openElements.indexOf(identifier) === -1);
    }, [openElements, identifier]);

    const handleClick = useCallback(() => {
        if (childrenItems.length > 0) {
            const hasActiveChildNow = hasActiveChild(childrenItems);
            const isCurrentlyOpen = openElements.indexOf(identifier) !== -1;

            // Empêcher la fermeture si un enfant est actif
            if (hasActiveChildNow && isCurrentlyOpen) {
                return;
            }

            const newOpenElements = [...openElements];
            const idx = newOpenElements.indexOf(identifier);

            if (idx !== -1) {
                newOpenElements.splice(idx, 1);
            } else {
                newOpenElements.push(identifier);
            }

            setOpenElements(newOpenElements);
        }

        if (onClick) {
            onClick(null);
        }

        if (downSmBreakpoint && onSidebarClose) {
            onSidebarClose();
        }
    }, [
        childrenItems,
        hasActiveChild,
        identifier,
        openElements,
        setOpenElements,
        onClick,
        downSmBreakpoint,
        onSidebarClose,
    ]);

    const containerStyle: CSSProperties = {
        fontWeight: activeElement || hover ? 600 : 500,
        paddingLeft: folded ? `4px` : `${(20 + (childrenAncestors.length - 1) * 15).toString()}px`,
        paddingRight: folded ? '0' : '10px',
        position: 'relative',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        minWidth: 0,
    };

    // Calculer la couleur de la bordure
    let borderColor = 'transparent';
    if (activeElement || hover) {
        borderColor = mainColor.main;
    } else if (openElements.indexOf(identifier) !== -1) {
        if (palette.mode === 'light') {
            borderColor = lighten(mainColor.main, 0.8);
        } else {
            borderColor = lighten(mainColor.main, 0.2);
        }
    }

    // Style pour la bordure active
    const activeBorderStyle: CSSProperties = {
        position: 'absolute',
        top: 0,
        right: '-20px',
        bottom: 0,
        width: '4px',
        backgroundColor: borderColor,
        zIndex: 2,
        pointerEvents: 'none',
    };

    let chevron = null;
    if (childrenItems.length > 0 && !folded) {
        chevron = collapsed ? (
            <Box sx={{ marginLeft: 'auto' }}>
                <FaOptionIcon
                    icon={['fasl', 'chevron-right']}
                    sx={{ display: 'flex', alignItems: 'center' }}
                />
            </Box>
        ) : (
            <Box sx={{ marginLeft: 'auto' }}>
                <FaOptionIcon
                    icon={['fasl', 'chevron-down']}
                    sx={{ display: 'flex', alignItems: 'center' }}
                />
            </Box>
        );
    }

    type IconPropsType = {
        size: string;
        color?: string;
        [key: string]: unknown;
    };

    type MinimalIcon = {
        iconName: string;
        iconProps: IconPropsType;
    };

    const isMinimalIcon = useCallback(
        (minimalIcon: unknown): minimalIcon is MinimalIcon =>
            typeof minimalIcon === 'object' &&
            minimalIcon !== null &&
            'iconName' in minimalIcon &&
            typeof (minimalIcon as MinimalIcon).iconName === 'string',
        [],
    );

    const iconProps = useMemo(() => {
        let foundProps: FaOptionIconProps | null = null;

        if (icon && Array.isArray(icon.icon)) {
            if (typeof icon.icon[1] === 'string') {
                foundProps = icon;
            } else if (typeof icon.icon[1] === 'object') {
                const namedIconProps = (icon.icon[1] as NamedIconsType).props;

                if (isMinimalIcon(namedIconProps.icon)) {
                    foundProps = {
                        icon: ['fasl', namedIconProps.icon.iconName as IconName],
                        iconProps: namedIconProps.iconProps,
                    };

                    if (isMinimalIcon(namedIconProps.option)) {
                        foundProps = {
                            ...foundProps,
                            option: ['fas', namedIconProps.option.iconName as IconName],
                            optionProps: namedIconProps.option.iconProps,
                        };
                    }
                }
            }
        }

        return foundProps;
    }, [icon, isMinimalIcon]);

    if (hidden) return null;

    if (header) {
        return (
            <Box
                className={boxClassNames.join(' ')}
                data-testid="modal-menu-item-header"
            >
                <Tooltip
                    title={folded ? [...ancestors, label].join(' -> ') : ''}
                    placement="right"
                >
                    <Typography
                        style={containerStyle}
                        variant="l2dcaption"
                        color="text.gris50"
                    >
                        {folded ? '' : label}
                    </Typography>
                </Tooltip>
            </Box>
        );
    }

    return (
        <Box
            className={boxClassNames.join(' ')}
            data-testid="modal-menu-item"
            sx={{ position: 'relative' }}
        >
            <Tooltip
                title={folded ? [...ancestors, label].join(' -> ') : label}
                placement="right"
            >
                <Box
                    onClick={handleClick}
                    className="menu-item-link"
                    data-testid="modal-menu-item-link"
                    style={containerStyle}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                >
                    {iconProps ? (
                        <Box sx={{ flexShrink: 1, marginRight: folded ? 0 : 1 }}>
                            <FaOptionIcon {...iconProps} />
                        </Box>
                    ) : null}

                    {/* Label avec ellipsis */}
                    {!folded && (
                        <Box
                            component="span"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                flexGrow: 1,
                            }}
                        >
                            {label}
                        </Box>
                    )}

                    {/* Chevron avec flex-shrink: 0 pour qu'il reste visible */}
                    {chevron && <Box sx={{ flexShrink: 0 }}>{chevron}</Box>}

                    <Box style={activeBorderStyle} />
                </Box>
            </Tooltip>
            <Collapse
                in={!collapsed}
                timeout="auto"
                unmountOnExit
            >
                {childrenNodes}
            </Collapse>
        </Box>
    );
};
